<!--
  ValueSelect.vue

  Form input for select / visibility fields. Native `<select>` driven by
  `field.values`. Reads `field.isArray` to flip on native `multiple`
  (and binds an array `modelValue` accordingly).

  Named to parallel `ReferenceSelect.vue` on the reference side — both
  are select-shaped form inputs, distinguished by what they pick from.
-->
<template>
  <div class="select" :class="selectClass">
    <select
      :value="formattedValue"
      :disabled="disabled || readonly"
      :multiple="!!field.isArray"
      @change="handleChange"
    >
      <option v-if="!field.isArray && placeholder" value="" disabled>
        {{ placeholder }}
      </option>
      <option v-for="opt in options" :key="String(opt.value)" :value="opt.value">
        {{ opt.label }}
      </option>
    </select>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { type Field, parseValue, selectOptions } from "@auger/core/fields"

const props = withDefaults(
  defineProps<{
    modelValue?: string | null
    field: Field
    placeholder?: string
    disabled?: boolean
    readonly?: boolean
    size?: "small" | "normal" | "medium" | "large"
  }>(),
  {
    modelValue: null,
    placeholder: "",
    disabled: false,
    readonly: false,
    size: "normal",
  }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
}>()

const formattedValue = computed(
  () => props.modelValue as string | number | (string | number)[] | null
)

const selectClass = computed(() => ({
  [`is-${props.size}`]: true,
  "is-multiple": !!props.field.isArray,
  "is-fullwidth": true,
}))

const options = computed(() => selectOptions(props.field))

function handleChange(event: Event) {
  const target = event.target as HTMLSelectElement
  if (props.field.isArray) {
    const selected = Array.from(target.selectedOptions).map((o) => o.value)
    emit("update:modelValue", parseValue(props.field, selected))
  } else {
    emit("update:modelValue", parseValue(props.field, target.value))
  }
}
</script>
